import { InternalId } from '@edgebox/data-definition-kit';
import { TaskDomain, TaskOperationSubType, TaskOperationType, TaskStatus, TaskType } from '@edgebox/sync-core-data-definitions';
import { Type, plainToInstance } from 'class-transformer';
import { ArrayMaxSize, ArrayMinSize, IsArray, IsString, Matches, MaxLength, ValidateNested } from 'class-validator';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Field } from 'formik';
import type { FieldProps } from 'formik';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { sendToParent } from '../../../frame-messages';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { BRAND_PRESENCE_ONBOARDING_TOTAL_STEPS } from './brandPresencePaths';
import {
  GovernanceAddAnotherPageButton,
  GovernanceAiSuggestedListItem,
  GovernanceAiSuggestionLabel,
  GovernanceOnboardingContainer,
  GovernanceOnboardingListItem,
  GovernanceOnboardingNewListItem,
  GovernanceOnboardingStepContainer,
} from './Onboarding/components/GovernanceOnboardingPrimitives';
import {
  DEFAULT_SUGGESTION_REASON,
  GovernanceOnboardingPathForm,
  GovernanceOnboardingPathInput,
  OnboardingSelectedUrlSuggestion,
  ensureHomeSuggestionFirst,
  getAiReasonFromSuggestion,
  getLinkNameByPath,
  normalizeConfidenceToPercent,
  normalizePageTitle,
  normalizePathInput,
  toAbsoluteUrl,
  toRelativePath,
} from './Onboarding/components/governanceOnboardingUrlSelection';

interface IKeyContentPage {
  path: string;
  title: string;
}

export interface IGovernanceOnboardingStep2Data {
  pages: IKeyContentPage[];
}

class GovernanceOnboardingStep2PageData implements IKeyContentPage {
  @IsString()
  @MaxLength(200)
  @Matches(/^\//)
  path!: string;

  @IsString()
  @MaxLength(200)
  title!: string;
}

export class GovernanceOnboardingStep2Data implements IGovernanceOnboardingStep2Data {
  @IsArray()
  @ArrayMinSize(3)
  @ArrayMaxSize(20)
  @ValidateNested({ each: true })
  @Type(() => GovernanceOnboardingStep2PageData)
  pages!: GovernanceOnboardingStep2PageData[];
}

interface IAiSuggestion extends IKeyContentPage {
  reason: string;
  confidence?: number | null;
}

interface INewPageRow {
  id: number;
  version: number;
}

function inferTaskGroupProgress(summary: Array<{ status: string; count: number }> | undefined): {
  progress: number;
  finished: boolean;
  message: string;
} {
  const items = summary || [];
  const total = items.reduce((sum, entry) => sum + (entry.count || 0), 0);

  if (!total) {
    return {
      progress: 5,
      finished: false,
      message: '0 pages processed',
    };
  }

  const doneStatusKeywords = ['finished', 'failed', 'aborted', 'skipped', 'limitexceeded', 'limit_exceeded'];

  const doneCount = items.reduce((sum, entry) => {
    const normalized = String(entry.status || '').toLowerCase();
    const isDone = doneStatusKeywords.some((keyword) => normalized.includes(keyword));
    return sum + (isDone ? entry.count || 0 : 0);
  }, 0);

  const rawProgress = Math.round((doneCount / total) * 100);
  const finished = doneCount >= total;
  const progress = finished ? 100 : Math.max(8, Math.min(98, rawProgress));

  return {
    progress,
    finished,
    message: `${doneCount} of ${total} pages processed`,
  };
}

async function sleep(ms: number) {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

export function GovernanceOnboardingStepKeyContent() {
  const navigate = useNavigate();
  const apiContext = React.useContext(SyncCoreApiContext);
  const params = useParams<{ onboardingId?: string }>();
  const [searchParams] = useSearchParams();
  const taskGroupId = params.onboardingId || searchParams.get('taskGroupId') || undefined;

  const [pages, setPages] = React.useState<IKeyContentPage[]>([]);
  const [suggestions, setSuggestions] = React.useState<Array<IAiSuggestion & { hidden: boolean }>>([]);
  const [editingPath, setEditingPath] = React.useState<string | undefined>(undefined);
  const [newPageRows, setNewPageRows] = React.useState<INewPageRow[]>([]);
  const [nextNewPageRowId, setNextNewPageRowId] = React.useState(0);
  const [loadingRecommendations, setLoadingRecommendations] = React.useState(true);
  const [loadingWarning, setLoadingWarning] = React.useState<string | undefined>(undefined);
  const [loadingError, setLoadingError] = React.useState<string | undefined>(undefined);
  const [submittingSelection, setSubmittingSelection] = React.useState(false);
  const [siteBaseUrl, setSiteBaseUrl] = React.useState<string | undefined>(undefined);
  const [finishingSelection, setFinishingSelection] = React.useState(false);
  const [finishingProgress, setFinishingProgress] = React.useState(0);
  const [finishingStatus, setFinishingStatus] = React.useState('Starting…');

  const continueToTaxonomySelection = React.useCallback(async () => {
    if (!apiContext?.api || !taskGroupId) {
      return;
    }

    setFinishingSelection(true);
    for (let attempt = 0; attempt < 600; attempt += 1) {
      const taskGroup = await apiContext.api.service.taskGroups.itemWithSummary(taskGroupId as InternalId, true);
      const nextProgress = inferTaskGroupProgress(taskGroup.summary as Array<{ status: string; count: number }> | undefined);
      setFinishingProgress(nextProgress.progress);
      setFinishingStatus(nextProgress.message);

      if (nextProgress.finished) {
        sendToParent({
          type: 'update-path',
          path: `/brand-presence/onboarding/${encodeURIComponent(taskGroupId)}/taxonomies`,
        });
        navigate(`/brand-presence/onboarding/${encodeURIComponent(taskGroupId)}/taxonomies`);
        return;
      }

      await sleep(2_000);
    }

    throw new Error('Processing timed out. Please try again in a moment.');
  }, [apiContext?.api, navigate, taskGroupId]);

  React.useEffect(() => {
    if (!apiContext?.api || !taskGroupId) {
      setLoadingRecommendations(false);
      if (!taskGroupId) {
        setLoadingError('Missing onboarding task group. Please start from Step 1.');
      }
      return;
    }

    let active = true;

    void (async () => {
      setLoadingRecommendations(true);
      setLoadingWarning(undefined);
      setLoadingError(undefined);

      const wait = async () => {
        await sleep(1000);
      };

      const getLatestTask = async () => {
        const paged = await apiContext.api.service.tasks.list(
          {
            domains: [TaskDomain.Governance],
            migrationId: taskGroupId as InternalId,
            types: [TaskType.Onboarding],
          },
          {
            page: 0,
            itemsPerPage: 20,
          }
        );

        return [...paged.items].sort((a, b) => String(b.id).localeCompare(String(a.id)))[0];
      };

      const getExtractOperation = (task: NonNullable<Awaited<ReturnType<typeof getLatestTask>>>) => {
        const operations = task.operations || [];
        const withMainPageSubtype = [...operations]
          .reverse()
          .find(
            (operation) => operation.type === TaskOperationType.Extract && operation.subType === TaskOperationSubType.ExtractMainPageData
          );
        if (withMainPageSubtype) {
          return withMainPageSubtype;
        }

        return [...operations].reverse().find((operation) => operation.type === TaskOperationType.Extract);
      };

      for (let attempt = 0; attempt < 120 && active; attempt += 1) {
        try {
          const latestTask = await getLatestTask();
          if (!latestTask) {
            await wait();
            continue;
          }

          const pendingOperation = [...(latestTask.operations || [])]
            .reverse()
            .find((operation) => operation.type === TaskOperationType.Pending);

          if ((pendingOperation?.status as TaskStatus | undefined) === TaskStatus.Finished) {
            setLoadingRecommendations(false);
            await continueToTaxonomySelection();
            return;
          }

          const extractOperation = getExtractOperation(latestTask);
          if (!extractOperation) {
            await wait();
            continue;
          }

          if ([TaskStatus.Initializing, TaskStatus.Running].includes(extractOperation.status as TaskStatus)) {
            setLoadingWarning(undefined);
            await wait();
            continue;
          }

          if ((extractOperation.status as TaskStatus) === TaskStatus.Retrying) {
            setLoadingWarning("We couldn't crawl your site yet, but we're still trying.");
            await wait();
            continue;
          }

          if (
            [TaskStatus.Failed, TaskStatus.Aborted, TaskStatus.LimitExceeded, TaskStatus.Skipped].includes(
              extractOperation.status as TaskStatus
            )
          ) {
            throw new Error("We're not able to crawl your site right now. Please contact our support for assistance.");
          }

          if ((extractOperation.status as TaskStatus) !== TaskStatus.Finished) {
            await wait();
            continue;
          }

          setLoadingWarning(undefined);

          const extraction = await apiContext.api.governance.onboarding.mainPageDataExtraction({
            taskGroupId: taskGroupId as InternalId,
          });

          if (!active) {
            return;
          }

          const baseUrl = extraction.baseUrlResolution?.finalHomePageUrl || extraction.baseUrlResolution?.originalSiteBaseUrl;
          setSiteBaseUrl(baseUrl);

          const selectedUrlSuggestions = extraction.selectedUrlSuggestions || [];

          const linkNameByPath = getLinkNameByPath(extraction.links, baseUrl);

          const mappedSuggestions = selectedUrlSuggestions
            .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))
            .slice(0, 20)
            .map((item: OnboardingSelectedUrlSuggestion): IAiSuggestion & { hidden: boolean } => {
              const candidatePath =
                typeof item.url === 'string' ? toRelativePath(item.url, baseUrl) || normalizePathInput(item.url, baseUrl) : '/';

              const titleFromLinks = candidatePath === '/' ? 'Home' : linkNameByPath.get(candidatePath);
              const titleFromSuggestionName = typeof item.name === 'string' ? item.name.trim() : undefined;
              const titleFromUrl = typeof item.url === 'string' ? item.url : undefined;

              return {
                path: candidatePath,
                title: normalizePageTitle(candidatePath, titleFromLinks || titleFromSuggestionName || titleFromUrl),
                reason: getAiReasonFromSuggestion(item.reasoning),
                confidence: normalizeConfidenceToPercent(item.confidence),
                hidden: false,
              };
            });

          setSuggestions(
            ensureHomeSuggestionFirst(mappedSuggestions, () => ({
              path: '/',
              title: 'Home',
              reason: DEFAULT_SUGGESTION_REASON,
              confidence: 100,
              hidden: false,
            }))
          );
          setLoadingRecommendations(false);
          return;
        } catch (error) {
          if (!active) {
            return;
          }

          if ((error as Error | undefined)?.message?.includes("We're not able to crawl your site right now")) {
            setLoadingRecommendations(false);
            setLoadingWarning(undefined);
            setLoadingError((error as Error).message);
            return;
          }

          await wait();
        }
      }

      if (active) {
        setLoadingRecommendations(false);
        setLoadingError('Recommendations are not available yet. Please try again in a moment.');
      }
    })();

    return () => {
      active = false;
    };
  }, [apiContext?.api, continueToTaxonomySelection, taskGroupId]);

  const pagesCountLabel = `${pages.length}/20 pages added`;
  const shownSuggestions = suggestions.filter((item) => !item.hidden);
  const hiddenSuggestionsCount = suggestions.length - shownSuggestions.length;

  const onAcceptSuggestion = (item: IAiSuggestion) => {
    const normalizedPath = normalizePathInput(item.path, siteBaseUrl);
    setSuggestions((prev) => prev.filter((candidate) => candidate.path !== item.path));
    setPages((prev) => {
      if (prev.length >= 20 || prev.some((candidate) => candidate.path === normalizedPath)) {
        return prev;
      }
      return [...prev, { path: normalizedPath, title: normalizePageTitle(normalizedPath, item.title) }];
    });
  };

  const onIgnoreSuggestion = (item: IAiSuggestion) => {
    setSuggestions((prev) => prev.map((candidate) => (candidate.path === item.path ? { ...candidate, hidden: true } : candidate)));
  };

  const onDeleteNewPageRow = (rowId: number) => {
    setNewPageRows((prev) => prev.filter((row) => row.id !== rowId));
  };

  const onAddAnotherPageRow = () => {
    setNewPageRows((prev) => [...prev, { id: nextNewPageRowId, version: 0 }]);
    setNextNewPageRowId((prev) => prev + 1);
  };

  if (finishingSelection) {
    const indicatorSize = 220;
    const strokeWidth = 16;
    const radius = (indicatorSize - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference - (finishingProgress / 100) * circumference;

    return (
      <GovernanceOnboardingContainer>
        <div className="px-8 py-16 md:px-14 md:py-20">
          <div className="mx-auto max-w-2xl text-center" role="status" aria-live="polite">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Analyzing your website</h1>
            <p className="text-slate-500 text-lg leading-relaxed mb-10">
              We are processing your selected pages and building taxonomy recommendations. This can take a couple of minutes to complete.
            </p>

            <div className="mx-auto mb-8" style={{ width: indicatorSize, height: indicatorSize }}>
              <svg width={indicatorSize} height={indicatorSize} viewBox={`0 0 ${indicatorSize} ${indicatorSize}`}>
                <defs>
                  <linearGradient id="onboardingStep2ProgressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#009EE3" />
                    <stop offset="34%" stopColor="#7491ED" />
                    <stop offset="68%" stopColor="#D96ECC" />
                    <stop offset="100%" stopColor="#FF5479" />
                  </linearGradient>
                </defs>

                <circle cx={indicatorSize / 2} cy={indicatorSize / 2} r={radius} fill="none" stroke="#E2E8F0" strokeWidth={strokeWidth} />
                <circle
                  cx={indicatorSize / 2}
                  cy={indicatorSize / 2}
                  r={radius}
                  fill="none"
                  stroke="url(#onboardingStep2ProgressGradient)"
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  transform={`rotate(-90 ${indicatorSize / 2} ${indicatorSize / 2})`}
                />

                <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" className="fill-slate-900 text-5xl font-bold">
                  {finishingProgress}%
                </text>
              </svg>
            </div>

            <p className="text-slate-500 text-lg">{finishingStatus}</p>
          </div>
        </div>
      </GovernanceOnboardingContainer>
    );
  }

  return (
    <GovernanceOnboardingContainer>
      <GovernanceOnboardingStepContainer
        step={2}
        totalSteps={BRAND_PRESENCE_ONBOARDING_TOTAL_STEPS}
        title={'Select your key content'}
        description={
          'Add 3 to 20 pages that are most representative of your goals. Our AI suggestions are based on the onboarding analysis for your site.'
        }
        onBack={() => {
          sendToParent({
            type: 'update-path',
            path: '/brand-presence/onboarding/domain',
          });
          navigate('/brand-presence/onboarding/domain', { replace: true });
        }}
        onContinue={async () => {
          if (!apiContext?.api || !taskGroupId || pages.length < 3) {
            return;
          }

          setSubmittingSelection(true);
          setLoadingError(undefined);
          try {
            await apiContext.api.governance.onboarding.submitSelectedPages(
              {
                selectedUrls: pages.map((page) => toAbsoluteUrl(page.path, siteBaseUrl)),
              },
              {
                taskGroupId: taskGroupId as InternalId,
              }
            );

            await continueToTaxonomySelection();
          } catch (error) {
            setFinishingSelection(false);
            setLoadingError((error as Error)?.message || 'Failed to continue onboarding.');
          } finally {
            setSubmittingSelection(false);
          }
        }}
        continueLabel={submittingSelection ? 'Saving…' : 'Continue'}
        continueDisabled={submittingSelection || pages.length < 3 || loadingRecommendations || !!loadingError}
      >
        <div className="w-full">
          {loadingRecommendations ? (
            <div className="mb-6 flex items-center gap-2 text-sm text-slate-600" role="status" aria-live="polite">
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Loading recommended URLs…</span>
            </div>
          ) : null}

          {loadingWarning ? <div className="mb-6 text-sm text-warning">{loadingWarning}</div> : null}

          {loadingError ? <div className="mb-6 text-sm text-danger">{loadingError}</div> : null}

          <div className="hidden md:grid grid-cols-12 gap-4 px-4 mb-2 text-left">
            <div className="col-span-4 text-sm font-semibold text-slate-700">Path</div>
            <div className="col-span-6 text-sm font-semibold text-slate-700">Page</div>
            <div className="col-span-2" />
          </div>

          <div className="space-y-3 mb-8">
            {shownSuggestions.map((item) => (
              <GovernanceAiSuggestedListItem
                key={item.path}
                onAccept={() => onAcceptSuggestion(item)}
                onIgnore={() => onIgnoreSuggestion(item)}
              >
                <div className="col-span-12 md:col-span-4 text-left pl-2">
                  <a
                    href={item.path}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary font-bold block truncate text-sm hover:underline"
                  >
                    {item.path}
                  </a>
                </div>
                <div className="col-span-12 md:col-span-6 text-left pl-2 md:pl-0">
                  <div className="flex items-center gap-2 min-w-0 flex-wrap md:flex-nowrap">
                    <div className="font-semibold text-slate-900 truncate">{item.title}</div>
                    <GovernanceAiSuggestionLabel />
                    <span className="inline-flex shrink-0 whitespace-nowrap rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-inset ring-slate-200">
                      {item.confidence}%
                    </span>
                  </div>
                  <div className="text-sm text-slate-500 truncate mt-1">{item.reason}</div>
                </div>
              </GovernanceAiSuggestedListItem>
            ))}

            {hiddenSuggestionsCount > 0 ? (
              <div className="px-2 text-sm text-slate-500">
                <span>{hiddenSuggestionsCount} hidden suggestions</span>
                <button
                  type="button"
                  className="ml-2 text-primary font-semibold hover:underline"
                  onClick={() => setSuggestions((prev) => prev.map((item) => ({ ...item, hidden: false })))}
                >
                  Show
                </button>
              </div>
            ) : null}

            {pages.map((page) =>
              editingPath === page.path ? (
                <GovernanceOnboardingPathForm
                  key={`edit-${page.path}`}
                  initialValues={plainToInstance(GovernanceOnboardingPathInput, { path: page.path })}
                  onSubmit={async (values) => {
                    const normalizedPath = normalizePathInput(values.path, siteBaseUrl);
                    setPages((prev) =>
                      prev.map((item) => {
                        if (item.path !== editingPath) {
                          return item;
                        }

                        return { path: normalizedPath, title: normalizePageTitle(normalizedPath, item.title) };
                      })
                    );
                    setEditingPath(undefined);
                  }}
                >
                  {() => (
                    <GovernanceOnboardingNewListItem
                      onDelete={() => setEditingPath(undefined)}
                      onSave={() => undefined}
                      saveButtonType={'submit'}
                    >
                      <div className="col-span-12 md:col-span-10">
                        <Field name={'path'}>
                          {({ field, form }: FieldProps<string>) => (
                            <input
                              {...field}
                              type={'text'}
                              placeholder={'/path/to/page or https://example.com/path'}
                              onBlur={(event) => {
                                const normalizedPath = normalizePathInput(event.target.value, siteBaseUrl);
                                form.setFieldValue('path', normalizedPath, false);
                                form.setFieldTouched('path', true, true);
                              }}
                              className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                            />
                          )}
                        </Field>
                      </div>
                    </GovernanceOnboardingNewListItem>
                  )}
                </GovernanceOnboardingPathForm>
              ) : (
                <GovernanceOnboardingListItem
                  key={page.path}
                  onDelete={() => setPages((prev) => prev.filter((candidate) => candidate.path !== page.path))}
                  onEdit={() => {
                    setEditingPath(page.path);
                  }}
                >
                  <div className="col-span-12 md:col-span-4 text-left">
                    <a href={page.path} target="_blank" rel="noreferrer" className="text-primary font-bold block truncate hover:underline">
                      {page.path}
                    </a>
                  </div>
                  <div className="col-span-12 md:col-span-6 text-left">
                    <div className="font-semibold text-slate-900 truncate">{normalizePageTitle(page.path, page.title)}</div>
                  </div>
                </GovernanceOnboardingListItem>
              )
            )}

            {newPageRows.map((row) => (
              <GovernanceOnboardingPathForm
                key={`new-${row.id}-${row.version}`}
                initialValues={plainToInstance(GovernanceOnboardingPathInput, { path: '' })}
                onSubmit={async (values) => {
                  const normalizedPath = normalizePathInput(values.path, siteBaseUrl);
                  let added = false;

                  setPages((prev) => {
                    if (prev.length >= 20 || prev.some((candidate) => candidate.path === normalizedPath)) {
                      return prev;
                    }

                    added = true;
                    return [...prev, { path: normalizedPath, title: normalizePageTitle(normalizedPath) }];
                  });

                  if (added) {
                    setNewPageRows((prev) =>
                      prev.map((candidate) => (candidate.id === row.id ? { ...candidate, version: candidate.version + 1 } : candidate))
                    );
                  }
                }}
              >
                {() => (
                  <GovernanceOnboardingNewListItem onDelete={() => onDeleteNewPageRow(row.id)}>
                    <div className="col-span-12 md:col-span-10">
                      <Field name={'path'}>
                        {({ field, form }: FieldProps<string>) => (
                          <input
                            {...field}
                            type={'text'}
                            placeholder={'/path/to/page or https://example.com/path'}
                            onBlur={(event) => {
                              const normalizedPath = normalizePathInput(event.target.value, siteBaseUrl);
                              form.setFieldValue('path', normalizedPath, false);
                              form.setFieldTouched('path', true, true);
                            }}
                            className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                          />
                        )}
                      </Field>
                    </div>
                  </GovernanceOnboardingNewListItem>
                )}
              </GovernanceOnboardingPathForm>
            ))}
          </div>

          <div className="flex justify-between items-center mt-6">
            <GovernanceAddAnotherPageButton onClick={onAddAnotherPageRow} />
            <span className="text-sm font-bold text-slate-500">{pagesCountLabel}</span>
          </div>
        </div>
      </GovernanceOnboardingStepContainer>
    </GovernanceOnboardingContainer>
  );
}
